/**
 * LocalGraphPanel - メモ・読書ノートの下に出す、そのページから 2 段以内のつながりの図。
 * 開いたときに初めてグラフを読み込む
 */

import { ChevronRight, Network } from 'lucide-react'
import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { getLinkGraph, type LinkGraph } from '@/api/services/links'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Spinner } from '@/components/ui/spinner'
import { graphNodePath, localSubgraph } from '@/lib/link-graph'
import { cn } from '@/lib/utils'
import { PATHS } from '@/routes/paths'

const LinkGraphCanvas = lazy(() => import('./link-graph-canvas'))

const GRAPH_HEIGHT = 280

type Props = {
  /** "memo:15" / "book:379208" */
  centerId: string
}

export const LocalGraphPanel = ({ centerId }: Props) => {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [graph, setGraph] = useState<LinkGraph | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    getLinkGraph()
      .then((g) => {
        if (!cancelled) setGraph(g)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [open, centerId])

  const local = useMemo(() => (graph ? localSubgraph(graph, centerId) : null), [graph, centerId])

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mx-6 mt-4 border-t pt-3 sm:mx-0">
      <div className="flex items-center justify-between">
        <CollapsibleTrigger className="flex cursor-pointer items-center gap-1.5 text-sm font-medium text-muted-foreground">
          <ChevronRight className={cn('size-4 transition-transform', open && 'rotate-90')} />
          つながり
        </CollapsibleTrigger>
        <Link
          to={PATHS.MEMO.GRAPH}
          className="flex items-center gap-1 rounded px-1 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <Network className="size-3.5" />
          全体のグラフ
        </Link>
      </div>
      <CollapsibleContent className="mt-2">
        {failed ? (
          <p className="py-6 text-center text-sm text-muted-foreground">読み込めませんでした</p>
        ) : !local ? (
          <div className="flex justify-center py-6">
            <Spinner size={20} />
          </div>
        ) : local.nodes.length <= 1 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            まだつながりがありません。「[[」でリンクするか、ハイライトを引用するとここに出ます
          </p>
        ) : (
          <Suspense fallback={<div style={{ height: GRAPH_HEIGHT }} />}>
            <LinkGraphCanvas
              graph={local}
              height={GRAPH_HEIGHT}
              centerId={centerId}
              onNodeClick={(node) => {
                const path = graphNodePath(node)
                if (path && node.id !== centerId) navigate(path)
              }}
            />
          </Suspense>
        )}
      </CollapsibleContent>
    </Collapsible>
  )
}
